import React, { useState } from "react";
import {
    DeviceMobile, Share, Plus, DotsThreeVertical, X,
    Lightning, ArrowsClockwise, WifiSlash, HardDrives, Sparkle,
} from "@phosphor-icons/react";
import InstallAppLink from "./InstallAppLink";
import { isIOS } from "../lib/installPrompt";

/**
 * InstallGuide — attendee/donor-facing "how to install + why a phone app beats
 * an App Store download" explainer. Reused in two places:
 *   - <InstallGuideContent/>  : the body, rendered inside the standalone
 *     /install-guide page and inside the modal below.
 *   - <InstallGuideTrigger/>  : a quiet "How to install & why →" text link that
 *     opens the content in a modal (dropped into DonorInstallStrip so it shows
 *     wherever the donor install band shows).
 *
 * No auto-popup — purely tap-to-open, per product decision.
 */

const BENEFITS = [
    { icon: DeviceMobile, title: "No app store needed", text: "Install straight from this page — nothing to search for or sign into." },
    { icon: Lightning, title: "No download wait", text: "It's ready in seconds, not minutes — perfect right at the door." },
    { icon: ArrowsClockwise, title: "Always up to date", text: "Updates itself automatically. Nothing to re-download, ever." },
    { icon: WifiSlash, title: "Works offline at the venue", text: "Bids, tickets and donations keep working if the banquet-hall Wi-Fi buckles." },
    { icon: HardDrives, title: "Almost no storage", text: "Uses a tiny fraction of what a normal App Store app takes up on your phone." },
    { icon: Sparkle, title: "Your org's icon on the home screen", text: "Opens full-screen, straight to this event — branded with the nonprofit's logo." },
];

function StepCard({ platform, steps, highlight }) {
    return (
        <div
            className={`border p-5 rounded-lg ${highlight ? "border-accent bg-accent/5" : "border-border bg-white"}`}
            data-testid={`install-steps-${platform.toLowerCase()}`}
        >
            <div className="flex items-center gap-2">
                <p className="font-heading text-base text-primary">{platform === "iPhone" ? "On iPhone / iPad" : "On Android"}</p>
                {highlight && <span className="text-[10px] font-bold uppercase tracking-wider bg-accent text-accent-foreground px-2 py-0.5 rounded-full">Your device</span>}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{platform === "iPhone" ? "Using Safari" : "Using Chrome"}</p>
            <ol className="mt-3 space-y-2.5 text-sm text-foreground list-none">
                {steps.map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                        <span className="shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                        <span>{s}</span>
                    </li>
                ))}
            </ol>
        </div>
    );
}

export function InstallGuideContent({ orgName = "", compact = false }) {
    const ios = isIOS();
    const iosSteps = [
        <>Tap the <Share size={14} weight="fill" className="inline -mt-0.5 text-primary" /> <span className="font-semibold">Share</span> button in Safari's toolbar.</>,
        <>Scroll down and choose <span className="font-semibold">Add to Home Screen</span> <Plus size={12} className="inline -mt-0.5" />.</>,
        <>Tap <span className="font-semibold">Add</span> in the top-right corner.</>,
    ];
    const androidSteps = [
        <>Tap the <DotsThreeVertical size={16} weight="bold" className="inline -mt-0.5 text-primary" /> menu in Chrome (top-right).</>,
        <>Choose <span className="font-semibold">Install app</span> or <span className="font-semibold">Add to Home screen</span>.</>,
        <>Tap <span className="font-semibold">Install</span> to confirm.</>,
    ];

    return (
        <div data-testid="install-guide-content">
            {!compact && (
                <div className="flex items-center gap-3">
                    <div className="shrink-0 w-12 h-12 rounded-lg bg-accent/15 text-accent flex items-center justify-center">
                        <DeviceMobile size={26} weight="fill" />
                    </div>
                    <div>
                        <p className="label-eyebrow text-[10px] text-accent">Add it to your phone</p>
                        <h2 className="font-heading text-2xl md:text-3xl text-primary leading-tight">
                            {orgName ? `Get ${orgName} on your home screen` : "Get this on your home screen"}
                        </h2>
                    </div>
                </div>
            )}

            <p className="text-sm text-muted-foreground mt-4 max-w-2xl">
                Installing takes about 10 seconds and skips the App Store entirely. You'll get a
                tappable icon that opens straight to {orgName ? `${orgName}'s` : "the"} event — bids,
                tickets and donations, all in one place.
            </p>

            {/* HOW TO — both platforms, your device highlighted */}
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <StepCard platform="iPhone" steps={iosSteps} highlight={ios} />
                <StepCard platform="Android" steps={androidSteps} highlight={!ios} />
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
                <InstallAppLink orgName={orgName} variant="button" />
                <span className="text-xs text-muted-foreground">
                    On Android/Chrome the button installs it instantly. On iPhone, follow the Safari steps above.
                </span>
            </div>

            {/* WHY — PWA vs App Store benefits */}
            <div className="mt-8">
                <p className="label-eyebrow text-[10px] text-accent">Why this beats a normal app download</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                    {BENEFITS.map((b, i) => (
                        <div key={i} className="border border-border rounded-lg p-4 bg-white" data-testid={`install-benefit-${i}`}>
                            <b.icon size={20} weight="fill" className="text-accent" />
                            <p className="font-heading text-sm text-primary mt-2">{b.title}</p>
                            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{b.text}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function InstallGuideTrigger({ orgName = "", className = "" }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className={`inline-flex items-center gap-1.5 text-xs text-primary-foreground/75 hover:text-accent underline underline-offset-4 transition-colors ${className}`.trim()}
                data-testid="install-guide-trigger"
            >
                <DeviceMobile size={13} weight="fill" /> How to install &amp; why
            </button>

            {open && (
                <div
                    className="fixed inset-0 z-[75] bg-black/55 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
                    role="dialog"
                    aria-modal="true"
                    data-testid="install-guide-modal"
                    onClick={() => setOpen(false)}
                >
                    <div
                        className="bg-background border-2 border-accent max-w-3xl w-full my-8 p-5 sm:p-7 relative rounded-lg"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setOpen(false)}
                            aria-label="Close"
                            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
                            data-testid="install-guide-close"
                        >
                            <X size={18} />
                        </button>
                        <InstallGuideContent orgName={orgName} />
                    </div>
                </div>
            )}
        </>
    );
}

export default InstallGuideContent;
